import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Assessment,
  FilterList,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ExaminationList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [examinations, setExaminations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);

  // Filter states
  const [filterCourse, setFilterCourse] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchExaminations();
  }, [showInactive]);

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      const coursesData = response.data.data || response.data || [];
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const fetchExaminations = async () => {
    try {
      setLoading(true);
      const params = { limit: 1000 };
      if (isSuperAdmin && showInactive) {
        params.isActive = 'false';
      }
      const response = await examinationAPI.getAll(params);
      const examsData = response.data.data || response.data || [];
      setExaminations(examsData);
    } catch (error) {
      console.error('Error fetching examinations:', error);
      toast.error('Failed to load examinations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await examinationAPI.delete(selectedExam._id);
      toast.success('Examination deleted successfully');
      setDeleteDialog(false);
      // Remove from local state immediately
      setExaminations(examinations.filter(exam => exam._id !== selectedExam._id));
      setSelectedExam(null);
    } catch (error) {
      console.error('Error deleting examination:', error);
      toast.error('Failed to delete examination');
    }
  };

  const handleToggleActive = async (examId, currentStatus) => {
    try {
      await examinationAPI.update(examId, { isActive: !currentStatus });
      toast.success(`Examination ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchExaminations();
    } catch (error) {
      console.error('Error toggling examination status:', error);
      toast.error('Failed to update examination status');
    }
  };

  const handleCourseChange = (courseId) => {
    setFilterCourse(courseId);
    setFilterSemester('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'info';
      case 'ongoing':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getExamStatus = (exam) => {
    const now = new Date();
    const start = new Date(exam.startDate);
    const end = new Date(exam.endDate);

    if (exam.status === 'cancelled') return 'cancelled';
    if (now < start) return 'upcoming';
    if (now > end) return 'completed';
    return 'ongoing';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateTotalMarks = (exam) => {
    if (!exam.subjects || exam.subjects.length === 0) return 0;
    return exam.subjects.reduce((sum, subject) => sum + (subject.totalMarks || 0), 0);
  };

  const calculateTotalPassingMarks = (exam) => {
    if (!exam.subjects || exam.subjects.length === 0) return 0;
    return exam.subjects.reduce((sum, subject) => sum + (subject.passingMarks || 0), 0);
  };

  const calculateTotalDuration = (exam) => {
    if (!exam.subjects || exam.subjects.length === 0) return 0;
    return exam.subjects.reduce((sum, subject) => sum + (subject.duration || 0), 0);
  };

  // Filter examinations
  const filteredExaminations = isSuperAdmin
    ? examinations.filter((exam) => {
        if (filterCourse && exam.course?._id !== filterCourse) return false;
        if (filterSemester && exam.semester?._id !== filterSemester) return false;
        return true;
      })
    : examinations.filter((exam) => exam.isActive !== false);

  // Get available semesters for selected course
  const filterSemesters = filterCourse
    ? courses.find((c) => c._id === filterCourse)?.semesters || []
    : [];

  // Group examinations by course and semester
  const groupedExaminations = filteredExaminations.reduce((acc, exam) => {
    const courseKey = exam.course?._id || 'unknown';
    const semesterKey = exam.semester?._id || exam.semester?.semesterName || 'unknown';

    if (!acc[courseKey]) {
      acc[courseKey] = {
        courseId: courseKey,
        courseName: exam.course?.name || 'Unknown Course',
        courseCode: exam.course?.code || '',
        semesters: {},
      };
    }

    if (!acc[courseKey].semesters[semesterKey]) {
      acc[courseKey].semesters[semesterKey] = {
        semesterName: exam.semester?.semesterName || 'Unknown Semester',
        examinations: [],
      };
    }

    acc[courseKey].semesters[semesterKey].examinations.push(exam);
    return acc;
  }, {});

  const courses_grouped = Object.values(groupedExaminations);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            All Examinations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Manage and monitor all examinations
          </Typography>
        </Box>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/examinations/create')}
          >
            Create Examination
          </Button>
        )}
      </Box>

      {/* Filters */}
      {isSuperAdmin && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FilterList />
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Filter by Course</InputLabel>
              <Select
                value={filterCourse}
                onChange={(e) => handleCourseChange(e.target.value)}
                label="Filter by Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {filterCourse && (
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Filter by Semester</InputLabel>
                <Select
                  value={filterSemester}
                  onChange={(e) => setFilterSemester(e.target.value)}
                  label="Filter by Semester"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  <MenuItem value="">All Semesters</MenuItem>
                  {filterSemesters.map((sem) => (
                    <MenuItem key={sem._id} value={sem._id}>
                      {sem.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Show Inactive</Typography>
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            </Stack>

            {(filterCourse || filterSemester) && (
              <Button
                size="small"
                onClick={() => {
                  setFilterCourse('');
                  setFilterSemester('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {filteredExaminations.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info">
            No examinations found. Click "Create Examination" to set up your first exam.
          </Alert>
        </Paper>
      ) : (
        <Box>
          {courses_grouped.map((course) => (
            <Accordion key={course.courseId} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {course.courseName}
                  </Typography>
                  <Chip label={course.courseCode} size="small" variant="outlined" />
                  <Chip
                    label={`${Object.values(course.semesters).reduce(
                      (sum, sem) => sum + sem.examinations.length,
                      0
                    )} Exams`}
                    size="small"
                    color="primary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {Object.values(course.semesters).map((semester, semIndex) => (
                    <Accordion key={semIndex} defaultExpanded elevation={3}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {semester.semesterName}
                          </Typography>
                          <Chip
                            label={`${semester.examinations.length} Exam${
                              semester.examinations.length !== 1 ? 's' : ''
                            }`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          {semester.examinations.map((exam, examIndex) => {
                            const status = getExamStatus(exam);
                            return (
                              <Card key={exam._id} elevation={2}>
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      mb: 2,
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                                        <Chip label={exam.code} size="small" color="primary" />
                                        <Chip
                                          label={status.toUpperCase()}
                                          size="small"
                                          color={getStatusColor(status)}
                                        />
                                        {isSuperAdmin && (
                                          <Chip
                                            label={exam.isActive !== false ? 'ACTIVE' : 'INACTIVE'}
                                            size="small"
                                            color={exam.isActive !== false ? 'success' : 'default'}
                                            variant="outlined"
                                          />
                                        )}
                                      </Stack>
                                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        {exam.name}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                      {isSuperAdmin && (
                                        <Tooltip
                                          title={
                                            exam.isActive !== false
                                              ? 'Deactivate examination'
                                              : 'Activate examination'
                                          }
                                        >
                                          <Switch
                                            checked={exam.isActive !== false}
                                            onChange={() =>
                                              handleToggleActive(exam._id, exam.isActive !== false)
                                            }
                                            color="primary"
                                            size="small"
                                          />
                                        </Tooltip>
                                      )}
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => navigate(`/admin/examinations/${exam._id}`)}
                                      >
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                      {isSuperAdmin && (
                                        <>
                                          <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                              navigate(`/admin/examinations/edit/${exam._id}`)
                                            }
                                          >
                                            <Edit fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => {
                                              setSelectedExam(exam);
                                              setDeleteDialog(true);
                                            }}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </>
                                      )}
                                      <IconButton
                                        color="info"
                                        size="small"
                                        onClick={() =>
                                          navigate(`/admin/examinations/${exam._id}/stats`)
                                        }
                                      >
                                        <Assessment fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        Start Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatDate(exam.startDate)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">
                                        End Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatDate(exam.endDate)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                      <Typography variant="caption" color="text.secondary">
                                        Total Marks
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {calculateTotalMarks(exam)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                      <Typography variant="caption" color="text.secondary">
                                        Passing Marks
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {calculateTotalPassingMarks(exam)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                      <Typography variant="caption" color="text.secondary">
                                        Duration
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {calculateTotalDuration(exam)} min
                                      </Typography>
                                    </Grid>
                                  </Grid>

                                  {exam.subjects && exam.subjects.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Subjects
                                      </Typography>
                                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" gap={1}>
                                        {exam.subjects.map((subject, idx) => (
                                          <Chip
                                            key={subject._id || idx}
                                            label={subject.name || subject.code}
                                            size="small"
                                            variant="outlined"
                                          />
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Examination</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete "{selectedExam?.name}"? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExaminationList;
